import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Scale,
  Shield,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

export const Legal = () => {
  const { api, user, isCOO } = useAuth();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);

  const fetchNotice = async () => {
    if (!isCOO) return;
    try {
      const response = await api.get('/portal/legal/proprietary-notice');
      setNotice(response.data);
    } catch (error) {
      console.error('Error fetching notice:', error);
      toast.error('Failed to load legal notice');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotice();
  }, [isCOO]);

  // Redirect if not COO
  if (!isCOO) {
    return <Navigate to="/portal" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="legal-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Legal & Compliance</h1>
          <p className="text-gray-500 mt-1">Proprietary technology notice & legal documents</p>
        </div>
      </div>

      {/* Proprietary Notice */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-gray-100"
      >
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-[#C6A87C]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#C6A87C]/10 flex items-center justify-center">
              <Scale className="w-6 h-6 text-[#C6A87C]" />
            </div>
            <div>
              <h2 className="font-heading text-xl">Proprietary System Notice</h2>
              <p className="text-sm text-gray-500">Complete Platform Loan Agreement</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Important Legal Notice</p>
              <p className="text-sm text-yellow-700 mt-1">
                Please read and acknowledge the following proprietary rights notice regarding this entire platform system.
              </p>
            </div>
          </div>

          <div className="prose max-w-none">
            <div className="bg-gray-50 border border-gray-200 p-6 font-serif text-gray-700 leading-relaxed whitespace-pre-wrap">
              {notice?.notice}
            </div>
          </div>

          {/* System Components List */}
          {notice?.system_components && (
            <div className="mt-6 p-4 bg-[#C6A87C]/5 border border-[#C6A87C]/20">
              <p className="font-medium text-gray-800 mb-3">Proprietary System Components:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {notice.system_components.map((component, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-[#C6A87C] rounded-full" />
                    {component}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Last Updated</p>
              <p className="font-medium">{notice?.last_updated}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Acknowledged By</p>
              <p className="font-medium">{notice?.acknowledged_by}</p>
            </div>
          </div>

          {!acknowledged && (
            <div className="mt-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="w-5 h-5 accent-[#C6A87C] mt-0.5"
                />
                <span className="text-sm text-gray-600">
                  I, <strong>{user?.name}</strong>, acknowledge that I have read, understood, and agree to the 
                  terms outlined in this Proprietary Technology Notice. I understand that the Virtual Augmentation 
                  Tool and associated intellectual property are the exclusive property of RJ Island Realty, LLC.
                </span>
              </label>
            </div>
          )}

          {acknowledged && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-6 p-4 bg-green-50 border border-green-200 flex items-center gap-3"
            >
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-700">
                Acknowledged on {new Date().toLocaleDateString()} by {user?.name}
              </span>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Additional Legal Documents */}
      <div className="bg-white border border-gray-100 p-6">
        <h2 className="font-heading text-xl mb-6">Company Legal Documents</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-100 p-4 hover:border-[#C6A87C] transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-[#C6A87C]" />
              <span className="font-medium">Operating Agreement</span>
            </div>
            <p className="text-sm text-gray-500 mb-3">Jax Remodeling Solutions LLC operating terms</p>
            <button className="text-xs text-[#C6A87C] font-semibold uppercase flex items-center gap-1 hover:underline">
              <Download className="w-3 h-3" /> Download PDF
            </button>
          </div>

          <div className="border border-gray-100 p-4 hover:border-[#C6A87C] transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-5 h-5 text-[#C6A87C]" />
              <span className="font-medium">Insurance Certificates</span>
            </div>
            <p className="text-sm text-gray-500 mb-3">Current liability and workers comp certificates</p>
            <button className="text-xs text-[#C6A87C] font-semibold uppercase flex items-center gap-1 hover:underline">
              <Download className="w-3 h-3" /> Download PDF
            </button>
          </div>

          <div className="border border-gray-100 p-4 hover:border-[#C6A87C] transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-[#C6A87C]" />
              <span className="font-medium">Contractor Licenses</span>
            </div>
            <p className="text-sm text-gray-500 mb-3">State and local contractor licensing</p>
            <button className="text-xs text-[#C6A87C] font-semibold uppercase flex items-center gap-1 hover:underline">
              <Download className="w-3 h-3" /> Download PDF
            </button>
          </div>

          <div className="border border-gray-100 p-4 hover:border-[#C6A87C] transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-[#C6A87C]" />
              <span className="font-medium">Standard Contract Templates</span>
            </div>
            <p className="text-sm text-gray-500 mb-3">Client contract and agreement templates</p>
            <button className="text-xs text-[#C6A87C] font-semibold uppercase flex items-center gap-1 hover:underline">
              <Download className="w-3 h-3" /> Download ZIP
            </button>
          </div>
        </div>
      </div>

      {/* Compliance Checklist */}
      <div className="bg-white border border-gray-100 p-6">
        <h2 className="font-heading text-xl mb-6">Compliance Checklist</h2>
        
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">General Liability Insurance - Current</span>
            <span className="ml-auto text-xs text-green-600">Exp: Dec 2026</span>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">Workers Compensation Insurance - Current</span>
            <span className="ml-auto text-xs text-green-600">Exp: Dec 2026</span>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">State Contractor License - Active</span>
            <span className="ml-auto text-xs text-green-600">FL #CBC1234567</span>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-100">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-700">Annual Business License Renewal</span>
            <span className="ml-auto text-xs text-yellow-600">Due: Jan 2026</span>
          </div>
        </div>
      </div>
    </div>
  );
};
